import axios from 'axios';
import { tokenUtils } from '../utils/tokenUtils';

class LabelsService {
  constructor() {
    this.apiUrl = process.env.REACT_APP_API_URL;
    this.endpoint = '/Label';

    axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          tokenUtils.clearAuth();
          window.location.href = '/';
        }
        return Promise.reject(error);
      }
    );
  }

  getAuthHeaders() {
    const token = tokenUtils.get();
    if (!token) throw new Error('No se encontró token de autenticación');
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  async getLabelById(id) {
    if (id == null || id === '') return null;
    const res = await axios.get(`${this.apiUrl}${this.endpoint}/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return res.data;
  }

  async getAllLabels() {
    const res = await axios.get(`${this.apiUrl}${this.endpoint}`, {
      headers: this.getAuthHeaders()
    });
    return res.data;
  }

  async getLabelGroups() {
    const res = await axios.get(`${this.apiUrl}/LabelGroup`, {
      headers: this.getAuthHeaders()
    });
    return res.data;
  }

  async getLabelGroupWithLabels(id) {
    const res = await axios.get(`${this.apiUrl}/LabelGroup/${id}/with-labels`, {
      headers: this.getAuthHeaders()
    });
    return res.data;
  }

  async createLabelGroup(labelGroupData) {
    const res = await axios.post(`${this.apiUrl}/LabelGroup`, labelGroupData, {
      headers: this.getAuthHeaders()
    });
    return res.data;
  }

  async addLabelToGroup(groupId, labelId) {
    const res = await axios.post(`${this.apiUrl}/LabelGroup/${groupId}/labels/${labelId}`, null, {
      headers: this.getAuthHeaders()
    });
    return res.data;
  }

  async removeLabelFromGroup(groupId, labelId) {
    const res = await axios.delete(`${this.apiUrl}/LabelGroup/${groupId}/labels/${labelId}`, {
      headers: this.getAuthHeaders()
    });
    return res.data;
  }

  async assignLabelGroups(userId, labelGroupIds) {
    const res = await axios.post(`${this.apiUrl}/User/assign-label-groups`, {
      userId,
      labelGroupIds,
    }, {
      headers: this.getAuthHeaders()
    });
    return res.data;
  }

  async getUserLabels(userId) {
    try {
      const response = await axios.get(`${this.apiUrl}/UserLabel/user/${userId}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async saveUserLabel(userId, labelId) {
    try {
      const response = await axios.post(`${this.apiUrl}/UserLabel`, 
        {
          userId,
          labelId
        }, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteUserLabel(id) {
    try {
      const response = await axios.delete(`${this.apiUrl}/UserLabel/${id}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async createLabel(labelData) {
    try {
      const response = await axios.post(`${this.apiUrl}${this.endpoint}`, labelData, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getLabelsForManagement() {
    const res = await axios.get(`${this.apiUrl}${this.endpoint}/management`, {
      headers: this.getAuthHeaders(),
    });
    return res.data;
  }

  async getLabelUsage(id) {
    const res = await axios.get(`${this.apiUrl}${this.endpoint}/${id}/usage`, {
      headers: this.getAuthHeaders(),
    });
    return res.data;
  }

  async updateLabel(id, labelData) {
    const res = await axios.put(`${this.apiUrl}${this.endpoint}/${id}`, labelData, {
      headers: this.getAuthHeaders(),
    });
    return res.data;
  }

  /**
   * Con force=false el backend responde 409 si la etiqueta está en uso.
   * Con force=true la desvincula de todos los registros antes de eliminarla.
   */
  async deleteLabel(id, force = false) {
    const res = await axios.delete(`${this.apiUrl}${this.endpoint}/${id}`, {
      headers: this.getAuthHeaders(),
      params: { force },
    });
    return res.data;
  }

  async getLabelGroupsOverview() {
    const res = await axios.get(`${this.apiUrl}/LabelGroup/overview`, {
      headers: this.getAuthHeaders(),
    });
    return res.data;
  }

  async updateLabelGroup(id, labelGroupData) {
    const res = await axios.put(`${this.apiUrl}/LabelGroup/${id}`, labelGroupData, {
      headers: this.getAuthHeaders(),
    });
    return res.data;
  }

  async deleteLabelGroup(id) {
    const res = await axios.delete(`${this.apiUrl}/LabelGroup/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return res.data;
  }
}

export default new LabelsService();